import { Button } from '@/components/ui/button'
import { Form } from '@/components/ui/form'
import { zodResolver } from '@hookform/resolvers/zod'
import { useForm } from 'react-hook-form'
import { useLocation, useNavigate } from 'react-router-dom'
import * as z from 'zod'
import { useMediaQueryLG } from '@/hooks/useMediaquery'
import { useMutation } from '@tanstack/react-query'
import { requestPasswordReset } from '@/api/authApi'
import { toast } from 'sonner'
import axios from 'axios'
// NOTE: Adjust this import path to wherever you defined these API calls.

const forgotPasswordSchema = z.object({
  email: z
    .string()
    .min(1, 'Email is required')
    .email('Please enter a valid email address')
    .refine(val => /\.[a-z]{2,}$/i.test(val), {
      message: 'Email must end with a valid domain',
    }),
})

export default function ResetPasswordMessage() {
  const navigate = useNavigate()
  const isMobile = useMediaQueryLG()
  const location = useLocation()
  const carriedEmail =
    (location.state as { email?: string } | null)?.email ??
    new URLSearchParams(location.search).get('email') ??
    ''

  const form = useForm<z.infer<typeof forgotPasswordSchema>>({
    resolver: zodResolver(forgotPasswordSchema),
    defaultValues: {
      email: carriedEmail,
    },
    mode: 'onChange',
  })

  const resendMutation = useMutation({
    mutationFn: requestPasswordReset,
    onSuccess: (_data, variables) => {
      navigate('/auth/login', {
        state: {
          message: 'Password reset email sent successfully!',
          email: variables.email,
        },
      })
      toast.success(_data.message)
    },
    onError: error => {
      if (axios.isAxiosError(error)) {
        const message =
          error?.response?.data?.message ||
          error?.message ||
          'Something went wrong. Please try again.'
        toast.error(message)
      }
    },
  })

  const handleSubmit = () => {
    const email = carriedEmail || form.getValues('email')
    if (!email) {
      navigate('/auth/reset-password')
      return
    }
    resendMutation.mutate({ email })
  }

  return (
    <main className="w-full" aria-label="Password reset message page">
      <header className="flex flex-col items-center lg:items-start gap-4 sm:gap-6 w-full">
        <img
          src="/auth/logo.svg"
          alt="SlopeSync Logo"
          className="w-28 sm:w-48"
        />
        <div className="flex flex-col items-center lg:items-start gap-2 sm:gap-3 w-full">
          <div className="text-center lg:text-left">
            <h1 className="text-xl lg:text-ss-lg leading-7 lg:leading-11 font-bold text-ss-gray-950 max-w-xl uppercase font-[ClashDisplay]">
              CHECK YOUR{' '}
              <span className="text-ss-blue-500 uppercase">
                REGISTERED EMAIL
              </span>
            </h1>
          </div>
          <p className="text-sm lg:text-base font-semibold text-ss-gray-600 max-w-md text-center lg:text-left">
            We sent a password reset link to your email. Please check your
            inbox. Incase you dont received tap resend
          </p>
        </div>
      </header>
      <Form {...form}>
        <form
          className="flex flex-col gap-6 w-full"
          onSubmit={form.handleSubmit(handleSubmit)}
          aria-label="Resend password reset form"
        >
          <Button
            onClick={() => navigate('/auth/create-password')}
            size={isMobile ? 'small' : 'large'}
            type="submit"
            className="w-full"
            disabled={resendMutation.isPending}
            aria-label="Resend password reset email"
          >
            {resendMutation.isPending ? 'Sending...' : 'Resend'}
          </Button>
        </form>
      </Form>
    </main>
  )
}
